/**
 * Bench archive loader — fetches static JSON + JSONL out of
 * `/replays/<bench-id>/...` and maps them onto the shapes the page
 * components consume.
 *
 * Engine wire format (canonical):
 *   tournament.json                       — Glicko ratings + match index
 *   matches/<a>_vs_<b>/match.json         — best-of-N outcome + battle list
 *   matches/<a>_vs_<b>/battle_<i>.jsonl   — per-turn engine frames
 *   transcripts/<agent>.jsonl             — LLM reasoning per turn
 *
 * For now the SPA bundles a single archive at
 * `public/replays/v2-bench-20-highlands/`. When the orchestrator
 * publishes more (or moves to a CDN bucket), this loader stays the
 * same — only `PUBLIC_REPLAY_BASE_URL` changes.
 */

import { parseBattle, type BattleFrame, type ParsedBattle } from "@pilot-protocol/replay-parser";
import { toolLabel, type LeaderboardRow } from "@pilot-protocol/ui";
import type { TranscriptEntry } from "@pilot-protocol/visual-engine";

export interface BenchIndexEntry {
  id: string;
  name: string;
  description?: string;
  startedAtUnixMs?: number;
  agents: ReadonlyArray<string>;
}

export interface BenchIndex {
  tournaments: ReadonlyArray<BenchIndexEntry>;
}

// ---- Engine wire shapes (subset) -------------------------------------

interface EngineRating {
  rating: number;
  rd: number;
  sigma: number;
}

interface EngineStanding {
  agent: string;
  rank: number;
  rating: number;
  rd: number;
  sigma: number;
}

interface EngineTournamentMatchRef {
  archive: string;
  pairing: string;
  reason: string;
  winner: "A" | "B" | "—";
  winner_agent?: string;
  wins_a: number;
  wins_b: number;
}

export interface EngineTournament {
  schema_version?: string;
  format?: string;
  best_of?: number;
  power_cap?: number;
  tau?: number;
  agents: ReadonlyArray<string>;
  agent_colors?: Record<string, string>;
  initial_ratings?: Record<string, EngineRating>;
  final_ratings?: Record<string, EngineRating>;
  standings?: ReadonlyArray<EngineStanding>;
  matches: ReadonlyArray<EngineTournamentMatchRef>;
  started_at_unix_ms?: number;
}

interface EngineDraft {
  picks: ReadonlyArray<number>;
  total_power?: number;
  filled_with?: number;
}

interface EngineMatchAgent {
  name: string;
  version?: string;
  display_color?: string;
  draft: EngineDraft;
}

interface EngineMatchBattleRef {
  index: number;
  replay: string;
  seed?: number;
  final_turn: number;
  final_hp_a?: number;
  final_hp_b?: number;
  winner: "A" | "B" | "—";
  reason: string;
}

interface EngineMatchOutcome {
  winner: "A" | "B" | "—";
  wins_a: number;
  wins_b: number;
  reason: string;
}

export interface EngineMatch {
  match_id: string;
  match_seed?: number;
  best_of: number;
  power_cap?: number;
  schema_version?: string;
  started_at_unix_ms?: number;
  ended_at_unix_ms?: number;
  agent_a: EngineMatchAgent;
  agent_b: EngineMatchAgent;
  battles: ReadonlyArray<EngineMatchBattleRef>;
  outcome: EngineMatchOutcome;
}

// ---- Transcript line (engine wire shape) -----------------------------
// Older bench transcripts use `kind` + `args` + `reasoning_content`; the
// overlay's ReasoningPanel wants the schema TranscriptEntry shape with
// `phase` + `response.reasoning_text`. Map between them on load.

interface EngineTranscriptLine {
  kind?: string;
  args?: Record<string, unknown> & { reasoning?: string };
  reasoning_content?: string;
  model?: string;
  match_seed?: number;
  latency_ms?: number;
  tokens_in?: number;
  tokens_out?: number;
  map_name?: string;
  system_prompt_chars?: number;
  user_prompt_chars?: number;
  turn?: number;
}

// ---- Base URL --------------------------------------------------------

function replayBaseUrl(): string {
  const env =
    (import.meta.env.PUBLIC_REPLAY_BASE_URL as string | undefined) ??
    (import.meta.env.VITE_REPLAY_BASE_URL as string | undefined);
  // Local dev / production bundle default: `/replays/` served by Vite
  // (or Cloudflare Pages) from `public/replays/`.
  return env ?? "/replays";
}

function trimSlash(s: string): string {
  return s.replace(/\/+$/, "");
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`fetch ${url} → ${res.status}`);
  return (await res.json()) as T;
}

async function fetchText(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${url} → ${res.status}`);
  return res.text();
}

function parseJsonl<T>(text: string): T[] {
  const out: T[] = [];
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      out.push(JSON.parse(trimmed) as T);
    } catch {
      // Skip malformed lines — bench archives are append-only and a
      // truncated final line is benign for the viewer.
    }
  }
  return out;
}

// ---- Public loaders --------------------------------------------------

export async function loadBenchIndex(): Promise<BenchIndex> {
  return fetchJson<BenchIndex>(`${trimSlash(replayBaseUrl())}/index.json`);
}

export async function loadTournament(id: string): Promise<EngineTournament> {
  return fetchJson<EngineTournament>(
    `${trimSlash(replayBaseUrl())}/${encodeURIComponent(id)}/tournament.json`,
  );
}

export async function loadMatch(
  tournamentId: string,
  archiveRelPath: string,
): Promise<EngineMatch> {
  return fetchJson<EngineMatch>(
    `${trimSlash(replayBaseUrl())}/${encodeURIComponent(tournamentId)}/${archiveRelPath}/match.json`,
  );
}

export async function loadBattle(
  tournamentId: string,
  archiveRelPath: string,
  replayFile: string,
): Promise<ParsedBattle> {
  const text = await fetchText(
    `${trimSlash(replayBaseUrl())}/${encodeURIComponent(tournamentId)}/${archiveRelPath}/${replayFile}`,
  );
  const frames = parseJsonl<BattleFrame>(text);
  return parseBattle(frames);
}

export async function loadTranscript(
  tournamentId: string,
  agentName: string,
  matchSeed?: number,
): Promise<TranscriptEntry[]> {
  const text = await fetchText(
    `${trimSlash(replayBaseUrl())}/${encodeURIComponent(tournamentId)}/transcripts/${encodeURIComponent(agentName)}.jsonl`,
  );
  const lines = parseJsonl<EngineTranscriptLine>(text);
  // Bench transcripts are global per agent across all matches in a
  // tournament. Filter by `match_seed` when known so the reasoning
  // panel doesn't crash matches together.
  const filtered =
    typeof matchSeed === "number"
      ? lines.filter((l) => l.match_seed === undefined || l.match_seed === matchSeed)
      : lines;
  return filtered.map((l): TranscriptEntry => {
    const phase = (l.kind ?? "decide") as TranscriptEntry["phase"];
    const reasoning =
      typeof l.args?.reasoning === "string"
        ? l.args.reasoning
        : typeof l.reasoning_content === "string" && l.reasoning_content.length > 0
          ? l.reasoning_content
          : "";
    const toolCalls = l.args
      ? [
          {
            name: phase.toUpperCase(),
            arguments: l.args as Record<string, unknown>,
          },
        ]
      : undefined;
    return {
      schema_version: "1.0.0",
      turn: typeof l.turn === "number" ? l.turn : 0,
      phase,
      llm: { model: l.model },
      response: {
        reasoning_text: reasoning,
        tool_calls: toolCalls,
        tokens_out: l.tokens_out,
        latency_ms: l.latency_ms,
      },
    };
  });
}

// ---- Derived shapes for the pages ------------------------------------

export interface LoadoutItem {
  name: string;
  label: string;
}

/** Convert a draft.picks[] integer list into label-friendly loadout
 *  rows — used by SideStatPanel's loadout slot. */
export function loadoutFromDraft(draft: EngineDraft): LoadoutItem[] {
  const counts = new Map<number, number>();
  for (const id of draft.picks) {
    if (id === 0) continue;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const out: LoadoutItem[] = [];
  for (const [id, count] of counts) {
    const label = toolLabel(id);
    const display = count > 1 ? `${label} ×${count}` : label;
    out.push({ name: label, label: display });
  }
  return out;
}

/** Engine tournament → public leaderboard rows (rank, rating, win-rate). */
export function leaderboardFromTournament(
  tournament: EngineTournament,
): LeaderboardRow[] {
  const standings = tournament.standings ?? [];
  // win counts = sum of wins_a/wins_b across matches the agent appears in
  const wins = new Map<string, number>();
  const matches = new Map<string, number>();
  for (const m of tournament.matches) {
    const [a, b] = m.pairing.split("_vs_");
    if (a) {
      matches.set(a, (matches.get(a) ?? 0) + 1);
      if (m.winner === "A") wins.set(a, (wins.get(a) ?? 0) + 1);
    }
    if (b) {
      matches.set(b, (matches.get(b) ?? 0) + 1);
      if (m.winner === "B") wins.set(b, (wins.get(b) ?? 0) + 1);
    }
  }
  return standings.map((s) => {
    const played = matches.get(s.agent) ?? 0;
    const won = wins.get(s.agent) ?? 0;
    return {
      rank: s.rank,
      handle: s.agent,
      league: "unbounded",
      rating: Math.round(s.rating),
      ratingCI: Math.round(s.rd),
      archetype: archetypeFor(s.agent),
      matches: played,
      winRate: played > 0 ? won / played : undefined,
    };
  });
}

function archetypeFor(
  agent: string,
): "sniper" | "brawler" | "scout" | "generalist" | undefined {
  switch (agent) {
    case "sniper":
    case "brawler":
    case "scout":
    case "generalist":
      return agent;
    default:
      return undefined;
  }
}
